import dayjs from 'dayjs'
import i18next from 'i18next'
import { cloneDeep, orderBy } from 'lodash'
import type { RecapQuoteInfoByTenantIdWithSortDaysQueryVariables } from 'src/API'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { graphqlApi } from 'src/redux/api/graphql/graphqlApi'
import type {
	ReduxPreparePurchase,
	ReduxQuote,
	ReduxRecapInfo,
	ReduxSuppliers,
	ReduxUserInfo,
} from 'src/types/apiType'
import type { QuoteType } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	fetchRecapInfoUtil,
	getCostlineFetchFilter,
} from 'src/utils/fetchUtils'
import { makeErrorToast, msg } from 'src/utils/makeToast'
import { captureExceptionAndThrow } from 'src/utils/posthogException'
import { getCreateQuoteRecapInfoUtil } from 'src/utils/recapGridUtils/createQuoteRecapInfoUtil/getCreateQuoteRecapInfoUtil'
import {
	getProperUpdateValues,
	hasBusinessChanges,
} from 'src/utils/updateApiUtil'

export const recapInfoApi = graphqlApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchRecapInfoByQuoteId: builder.query({
			query: ({
				quoteId,
				userInfo,
			}: {
				quoteId: string
				userInfo: ReduxUserInfo
			}) => ({
				queryName: 'recapInfoInfoByQuoteId',
				data: {
					quoteId,
					filter: getCostlineFetchFilter(userInfo),
					limit: 3000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxRecapInfo[]) => {
				const sortedRecapInfo = orderBy(
					response,
					['fractionalSortKey'],
					['asc']
				)
				return sortedRecapInfo
			},
			providesTags: (result) =>
				result && result.length > 0
					? [{ type: 'RecapInfo', id: result[0].quoteId }]
					: ['RecapInfo'],
		}),
		fetchRecapCostlineByTenantIdSortDays: builder.query({
			query: ({
				tenantId,
				filter,
			}: {
				tenantId: string
				filter: RecapQuoteInfoByTenantIdWithSortDaysQueryVariables['filter']
			}) => ({
				queryName: 'recapQuoteInfoByTenantIdWithSortDays',
				data: {
					tenantId: tenantId,
					days: { gt: 0 },
					filter,
					limit: 1000,
				},
				isList: true,
			}),
			transformResponse: (response: ReduxRecapInfo[]) => response,
			providesTags: (result) =>
				result
					? [
							...result.map(({ id }) => ({
								type: 'RecapInfo' as const,
								id: id as string,
							})),
							{ type: 'RecapInfo', id: 'LIST' },
						]
					: [{ type: 'RecapInfo', id: 'LIST' }],
		}),
		createInvoiceRecapCostline: builder.mutation({
			queryFn: async ({
				recapId,
				quoteId,
				quoteData,
				preparePurchaseData,
				supplierId,
				userInfo,
			}: {
				recapId: string
				quoteId: string
				quoteData: ReduxQuote
				preparePurchaseData?: ReduxPreparePurchase
				supplierId: string
				userInfo: ReduxUserInfo
			}) => {
				try {
					// Get supplier details and all recapInfo costlines
					const quoteFilter = getCostlineFetchFilter(userInfo)
					const {
						data: { getSuppliers: supplierDetails },
					} = (await API.graphql(
						graphqlOperation(queries.getSuppliers, {
							id: supplierId,
						})
					)) as { data: { getSuppliers: ReduxSuppliers } }

					const recapInfoCostlines: ReduxRecapInfo[] =
						await fetchRecapInfoUtil(quoteId, quoteFilter)

					const originalRows = cloneDeep(recapInfoCostlines)
					if (!quoteData.quoteType) {
						throw new Error('No quote type set')
					}
					const supplierUUidDetails = getSupplierUuidDetails({
						quoteType: quoteData.quoteType,
						supplierDetails,
						originalQuote: quoteData,
					})

					const recapSubCategoryTotalIndex =
						recapInfoCostlines.findIndex(
							(r) =>
								r.subCategoryUuid ===
									supplierUUidDetails?.subCategoryUuid &&
								r.costLineType === 'subCategoryTotal'
						)

					if (recapSubCategoryTotalIndex === -1) {
						captureExceptionAndThrow(
							new Error(
								`SubCategory Total costline not found for ${supplierUUidDetails?.subCategoryUuid}`
							),
							{
								location:
									'recapInfoApi.createRecapCostlineFromPreparePurchase',
								recapQuoteId: quoteData.id,
								subCategoryUuid:
									supplierUUidDetails?.subCategoryUuid,
								preparePurchaseId: preparePurchaseData?.id,
								supplierName: preparePurchaseData?.supplier,
							}
						)
					}
					const recapSubCategoryTotal =
						recapInfoCostlines[recapSubCategoryTotalIndex]

					const newRecapCostline = {
						id: recapId,
						quoteId: quoteData.id,
						tenantId: quoteData.tenantId,
						activeFlag: true,
						costLineType: 'costLine',
						freelancers: ['dummyId'],
						childFreelancers: quoteData.childFreelancers,
						item: `${preparePurchaseData?.supplier}-${dayjs(
							preparePurchaseData?.invoiceDate
						).format('DD-MM-YYYY')}`,
						item_fr: `${preparePurchaseData?.supplier}-${dayjs(
							preparePurchaseData?.invoiceDate
						).format('DD-MM-YYYY')}`,
						categoryUuid: supplierUUidDetails?.categoryUuid,
						subCategoryUuid: supplierUUidDetails?.subCategoryUuid,
						category: supplierUUidDetails?.categoryName,
						category_fr: supplierUUidDetails?.categoryName_fr,
						subCategory: supplierUUidDetails?.subCategoryName,
						subCategory_fr: supplierUUidDetails?.subCategoryName_fr,
						categoryId: recapSubCategoryTotal?.categoryId,
						categoryPrefix: recapSubCategoryTotal?.categoryPrefix,
						subCategoryId: recapSubCategoryTotal?.subCategoryId,
						subCategoryPrefix:
							recapSubCategoryTotal?.subCategoryPrefix,
						subCategorySuffix:
							recapSubCategoryTotal?.subCategorySuffix,
						newRow: true,
					} as ReduxRecapInfo & { newRow?: boolean }
					// Insert the recap costline in the right spot
					recapInfoCostlines.splice(
						recapSubCategoryTotalIndex,
						0,
						newRecapCostline
					)
					// Calculate the tableRowIndex and id
					const { createRecapInfoCostLineIdsAndLabels } =
						getCreateQuoteRecapInfoUtil(quoteData.quoteType)
					const updatedRecapInfo =
						createRecapInfoCostLineIdsAndLabels(
							recapInfoCostlines,
							quoteData.id as string,
							quoteData.tenantId as string,
							quoteData.childFreelancers as string[]
						)

					// Create RecapDetails and update TableRowIndex
					const createOrUpdateRecapPromiseArray = []
					for (let i = 0; i < updatedRecapInfo.length; i++) {
						// @ts-expect-error
						const { newRow = false, ...eachRow } =
							updatedRecapInfo[i]
						const originalRow = originalRows.find(
							(row) => row.id === eachRow.id
						)
						if (!newRow && originalRow) {
							const updateRecapValues =
								getProperUpdateValues<ReduxRecapInfo>({
									updatedInput: eachRow,
									originalInput: originalRow,
								})

							if (hasBusinessChanges(updateRecapValues)) {
								async function updateRecap() {
									await API.graphql(
										graphqlOperation(
											mutations.updateRecapInfo,
											{ input: updateRecapValues }
										)
									)
								}
								createOrUpdateRecapPromiseArray.push(
									updateRecap()
								)
							}
						} else {
							await API.graphql(
								graphqlOperation(mutations.createRecapInfo, {
									input: eachRow,
								})
							)
						}
					}
					await Promise.all(createOrUpdateRecapPromiseArray)
					return { data: '' }
				} catch (error: any) {
					makeErrorToast(msg)
					return { error }
				}
			},
		}),
		// Modelled as a query (not a mutation) so RTK Query handles the
		// once-per-quote firing for us: subscriptions are deduped by arg, so the
		// three recap pages mounting for the same quoteId trigger a single sync,
		// and it is cached rather than re-fired on remount/navigation. This is
		// only safe because sync is now idempotent (deterministic recap ids), so
		// re-running it can never create duplicates.
		syncRecapInfo: builder.query({
			queryFn: async ({ quoteId }: { quoteId: string }) => {
				try {
					await API.graphql(
						graphqlOperation(mutations.syncRecapInfo, { quoteId })
					)
					return { data: '' }
				} catch (err) {
					const message = getErrorMessage(err)
					const tQuoteGridPage1 = i18next.getFixedT(
						null,
						'QuoteGridPage1'
					)
					makeErrorToast(tQuoteGridPage1('syncLambdaError'))
					console.error('refreshRecapQuoteInfo message: ', message)
					return { error: message }
				}
			},
			// Queries can't declare invalidatesTags, so invalidate after the sync
			// resolves to refetch the recap grid + totals. This endpoint
			// intentionally provides NO tags — tagging it with RecapInfo would
			// make the invalidation refetch the sync itself and loop forever.
			async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
				try {
					await queryFulfilled
					dispatch(
						graphqlApi.util.invalidateTags([
							'QuoteTotals',
							'RecapInfo',
						])
					)
				} catch {
					// Sync failed; the error was already surfaced via toast in
					// queryFn, so there is nothing to invalidate.
				}
			},
		}),
	}),
	overrideExisting: false,
})

function getSupplierUuidDetails({
	quoteType,
	supplierDetails,
	originalQuote,
}: {
	quoteType: QuoteType
	supplierDetails: ReduxSuppliers
	originalQuote: ReduxQuote
}) {
	switch (quoteType) {
		case 'frenchApfp':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'frenchApfp'
			)
		case 'frenchPostProd':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'frenchPostProd'
			)
		case 'aicp':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'aicp'
			)
		case 'photoGrid':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'photoGrid'
			)
		case 'soundProd':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'soundProd'
			)
		case 'cnc5ch':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'cnc5ch'
			)
		case 'quoteAnimationOnly':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'quoteAnimationOnly'
			)
		case 'quoteAnimationAndLiveAction':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'quoteAnimationAndLiveAction'
			)
		case 'blankQuote':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === originalQuote.blankQuoteType
			)
		case 'quoteDigital':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === 'quoteDigital'
			)
		case '360Template':
			return supplierDetails.quoteSectionDetails?.find(
				(q) => q?.quoteType === '360Template'
			)

		default: {
			const tsCheck: never = quoteType
		}
		// do nothing
	}
}

export const {
	useFetchRecapInfoByQuoteIdQuery,
	useFetchRecapCostlineByTenantIdSortDaysQuery,
	useCreateInvoiceRecapCostlineMutation,
	useSyncRecapInfoQuery,
	useLazySyncRecapInfoQuery,
} = recapInfoApi
